import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    intent: "Work",
  });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({ ...form, role: "customer" });
      toast.success("Account created");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e?.target ? e.target.value : e });

  return (
    <div className="min-h-screen bg-[#0A0908] relative flex items-center justify-center p-6">
      <Link
        to="/"
        className="absolute top-6 left-6 flex items-center gap-2 text-zinc-400 hover:text-white text-sm"
        data-testid="register-back-link"
      >
        <ArrowLeft size={16} /> Back
      </Link>
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-2xl p-10">
        <h1 className="text-3xl font-black tracking-tight mb-2">Join Mirrorloops.</h1>
        <p className="text-zinc-400 text-sm mb-8">
          Book seats, reserve Macs, unlock memberships.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Full name</Label>
            <Input
              data-testid="register-name-input"
              required
              value={form.name}
              onChange={set("name")}
              className="mt-2 bg-white/5 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</Label>
            <Input
              type="email"
              data-testid="register-email-input"
              required
              value={form.email}
              onChange={set("email")}
              className="mt-2 bg-white/5 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Phone</Label>
            <Input
              data-testid="register-phone-input"
              required
              value={form.phone}
              onChange={set("phone")}
              className="mt-2 bg-white/5 border-white/10"
              placeholder="+91-"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Password</Label>
            <Input
              type="password"
              data-testid="register-password-input"
              required
              minLength={6}
              value={form.password}
              onChange={set("password")}
              className="mt-2 bg-white/5 border-white/10"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
              Primary intent
            </Label>
            <Select value={form.intent} onValueChange={set("intent")}>
              <SelectTrigger
                data-testid="register-intent-select"
                className="mt-2 bg-white/5 border-white/10"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["Work", "Study", "Gaming", "Coworking", "Courses"].map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="register-submit-btn"
            className="w-full bg-[#D4B878] hover:bg-[#E8D6A3] rounded-full py-6 font-bold"
          >
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>

        <div className="mt-6 text-sm text-zinc-400 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-[#E8D6A3] font-semibold">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
